import { getHalfYearNumber, getQuarterNumber } from '@toggle/helpers';
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  startOfWeek,
} from 'date-fns';

import { XAxisTicks } from '~/types/axis.types';
import { CreateChartOptions } from '~/types/create.types';
import { TsDataPoint } from '~/types/timeseries.types';

import { i18n } from '../i18n';

interface CalculateXAxisTicksProps {
  data: TsDataPoint[];
  domain: [number, number];
  spaceBetweenTicks: number;
}

interface TicksRangeConfig {
  year: boolean;
  month: boolean;
  condition: (date: Date, lastDate: Date) => number;
  format: (date: Date) => string;
}

type TicksConfig = {
  [x in TicksRange]: TicksRangeConfig;
};

export enum TicksRange {
  daily,
  weekly,
  monthly,
  eachQuarter,
  eachHalfYear,
  eachThreeYears,
}

const mapTicksRangeToConfig: TicksConfig = {
  [TicksRange.daily]: {
    year: true,
    month: true,
    condition: date => date.getUTCDate(),
    format: date => date.getUTCDate().toString(),
  },
  [TicksRange.weekly]: {
    year: true,
    month: true,
    condition: date => startOfWeek(date).getTime(),
    format: date => date.getUTCDate().toString(),
  },
  [TicksRange.monthly]: {
    year: true,
    month: false,
    condition: date => date.getUTCMonth(),
    format: date =>
      date.toLocaleString(i18n.resolvedLanguage, { month: 'short' }),
  },
  [TicksRange.eachQuarter]: {
    year: true,
    month: false,
    condition: getQuarterNumber,
    format: date =>
      date.toLocaleString(i18n.resolvedLanguage, { month: 'short' }),
  },
  [TicksRange.eachHalfYear]: {
    year: true,
    month: false,
    condition: getHalfYearNumber,
    format: date =>
      date.toLocaleString(i18n.resolvedLanguage, { month: 'short' }),
  },
  [TicksRange.eachThreeYears]: {
    year: false,
    month: false,
    condition: (date, lastDate) =>
      Math.ceil((lastDate.getUTCFullYear() - date.getUTCFullYear()) / 3),
    format: date => date.getUTCFullYear().toString(),
  },
};

export const getTicksRange = (
  data: TsDataPoint[],
  domain: [number, number]
) => {
  const start = new Date(data[domain[0]].time);
  const end = new Date(data[domain[1]].time);
  const monthBetween = Math.abs(differenceInCalendarMonths(end, start));
  const yearsBetween = end.getFullYear() - start.getFullYear();

  if (differenceInCalendarDays(end, start) < 21) {
    return TicksRange.daily;
  }

  if (monthBetween < 6) {
    return TicksRange.weekly;
  }

  if (monthBetween < 12) {
    return TicksRange.monthly;
  }

  if (yearsBetween < 3) {
    return TicksRange.eachQuarter;
  }

  if (yearsBetween < 10) {
    return TicksRange.eachHalfYear;
  }

  return TicksRange.eachThreeYears;
};

export const createTicksFromRange = (
  ticksRange: TicksRange,
  data: TsDataPoint[],
  domain: [number, number]
) => {
  const config = mapTicksRangeToConfig[ticksRange];
  const lastDate = new Date(data[data.length - 1].time);
  const ticks = [];

  for (let index = domain[0] + 1; index <= domain[1]; index++) {
    const date = new Date(data[index - 1].time);
    const currentDate = new Date(data[index].time);
    const firstValue = config.condition(date, lastDate);
    const secondValue = config.condition(currentDate, lastDate);

    if (firstValue === secondValue) {
      continue;
    }

    let label,
      highlight = false;

    if (config.year && currentDate.getFullYear() - date.getFullYear() === 1) {
      label = currentDate.getFullYear().toString();
      highlight = true;
    } else if (config.month && currentDate.getMonth() - date.getMonth() === 1) {
      label = mapTicksRangeToConfig[TicksRange.monthly].format(currentDate);
      highlight = true;
    } else {
      label = config.format(currentDate);
    }

    ticks.push({ index, label, highlight });
  }

  return ticks;
};

export const calculateXAxisTicks = ({
  data,
  domain,
  spaceBetweenTicks,
}: CalculateXAxisTicksProps): XAxisTicks => {
  const ticksRange = getTicksRange(data, domain);
  const ticks = createTicksFromRange(ticksRange, data, domain);
  return { ticks, spaceBetweenTicks };
};

interface IndexToCoordinateProps {
  options: CreateChartOptions;
  latestTickIndex: number;
  tickIndex: number;
  spaceBetweenTicks: number;
}

export const indexToCoordinate = ({
  options,
  latestTickIndex,
  tickIndex,
  spaceBetweenTicks,
}: IndexToCoordinateProps) => {
  const deltaFromRight = latestTickIndex - tickIndex;
  const coordinate =
    options.width -
    options.gutters.y -
    (deltaFromRight + 0.5) * spaceBetweenTicks -
    1;
  return coordinate;
};
